/**
 * Sprint 17 B1: Journey List Page — wired to API hook, brand-compliant colors
 * Shows all journeys for the tenant with status, stats, and actions.
 */
import { useTranslation } from "@/i18n";
import { Link } from "react-router";
import { Route as RouteIcon, Plus } from "lucide-react";
import { useJourneys } from "@/hooks/use-journeys";
import type { JourneyStatus } from "@/components/journey/types";
import { formatDate, formatNumber } from "@/lib/i18n";

const STATUS_STYLES: Record<JourneyStatus, string> = {
  draft: "bg-[var(--color-surface)] text-[var(--color-muted-foreground)]",
  active: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  paused: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  archived: "bg-[var(--color-error)]/10 text-[var(--color-error)]",
};

export default function JourneyListPage() {
  const { t } = useTranslation();
  const { journeys, isLoading } = useJourneys();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">
            {t("journey.list.title")}
          </h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t("journey.list.description")}
          </p>
        </div>
        <Link
          to="/communications/journeys/builder"
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
        >
          <Plus className="h-4 w-4" />
          {t("journey.list.createNew")}
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-[var(--color-surface)] animate-pulse" />
          ))}
        </div>
      ) : journeys.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] py-16">
          <RouteIcon className="h-12 w-12 text-[var(--color-muted-foreground)] mb-4" />
          <h3 className="text-lg font-medium text-[var(--color-foreground)]">
            {t("journey.list.emptyTitle")}
          </h3>
          <p className="text-sm text-[var(--color-muted-foreground)] max-w-sm text-center mt-1">
            {t("journey.list.emptyDescription")}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--color-border)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
                <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">
                  {t("journey.list.colName")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">
                  {t("journey.list.colStatus")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">
                  {t("journey.list.colNodes")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-[var(--color-muted-foreground)]">
                  {t("journey.list.colUpdated")}
                </th>
              </tr>
            </thead>
            <tbody>
              {journeys.map((j) => (
                <tr key={j.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface)] cursor-pointer">
                  <td className="px-4 py-3">
                    <Link
                      to={`/workflows/automations?workflowId=${j.id}`}
                      className="font-medium text-[var(--color-foreground)] hover:text-[var(--color-primary)]"
                    >
                      {j.name}
                    </Link>
                    {j.description && (
                      <div className="text-xs text-[var(--color-muted-foreground)] mt-0.5">
                        {j.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[j.status]}`}>
                      {t(`journey.status.${j.status}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted-foreground)]">
                    {formatNumber(j.nodes.length)}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted-foreground)] text-xs">
                    {formatDate(j.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
